import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider,
  Stack,
  Link
} from '@mui/material';
import {
  //Integration,
  IntegrationInstructions,
  SettingsEthernet,
  Check,
  Warning,
  Settings,
  Hub,
  Notifications,
  Chat,
  People,
  Launch,
  Refresh,
  Delete,
  Info
} from '@mui/icons-material';
import api, { slackAPI } from '../services/api';

interface SlackIntegration {
  id: number;
  team_id?: string;
  team_name?: string;
  is_enabled: boolean;
  default_channel?: string;
  enable_conversation_notifications: boolean;
  enable_mention_notifications: boolean;
  enable_message_replies: boolean;
  enable_status_updates: boolean;
  notification_settings: Record<string, boolean>;
  channel_mappings: Record<string, string>;
  installed_at: string;
}

interface SlackChannel {
  id: string;
  name: string;
  is_private: boolean;
  is_member: boolean;
}

const SlackIntegrationPage: React.FC = () => {
  const [integration, setIntegration] = useState<SlackIntegration | null>(null);
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    client_secret: '',
    signing_secret: '',
    default_channel: '',
    enable_conversation_notifications: true,
    enable_mention_notifications: true,
    enable_message_replies: true,
    enable_status_updates: true,
    notification_settings: {
      new_conversation: true,
      conversation_assignment: true,
      mention_in_note: true,
      conversation_resolved: false,
      high_priority_conversation: true
    }
  });

  useEffect(() => {
    fetchIntegration();
  }, []);

  const fetchIntegration = async () => {
    try {
      setLoading(true);
      const response = await api.get('/slack/');
      setIntegration(response.data);
      setFormData(prev => ({ ...prev, ...response.data }));
      
      // Fetch channels if integration exists
      fetchChannels();
    } catch (error) {
      console.error('Error fetching integration:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChannels = async () => {
    try {
      const data = await slackAPI.getChannels();
      setChannels(data.channels || []);
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  };

  const handleCreateIntegration = async () => {
    try {
      await api.post('/slack/', {
        client_id: formData.client_id,
        client_secret: formData.client_secret,
        signing_secret: formData.signing_secret,
        default_channel: formData.default_channel,
        enable_conversation_notifications: formData.enable_conversation_notifications,
        enable_mention_notifications: formData.enable_mention_notifications,
        enable_message_replies: formData.enable_message_replies,
        enable_status_updates: formData.enable_status_updates
      });

      setIsConfigDialogOpen(false);
      setActiveStep(1);
      fetchIntegration();
    } catch (error) {
      console.error('Error creating integration:', error);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      const response = await fetch('/api/v1/slack/', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          default_channel: formData.default_channel,
          enable_conversation_notifications: formData.enable_conversation_notifications,
          enable_mention_notifications: formData.enable_mention_notifications,
          enable_message_replies: formData.enable_message_replies,
          enable_status_updates: formData.enable_status_updates,
          notification_settings: formData.notification_settings
        })
      });

      if (response.ok) {
        fetchIntegration();
      }
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const handleOAuthConnect = async () => {
    try {
      const response = await fetch('/api/v1/slack/oauth/url', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        window.open(data.oauth_url, '_blank');
        setActiveStep(2);
      }
    } catch (error) {
      console.error('Error getting OAuth URL:', error);
    }
  };

  const handleTestIntegration = async () => {
    try {
      setTesting(true);
      const response = await fetch('/api/v1/slack/test', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Test message sent successfully! Check your Slack channel.');
        setActiveStep(3);
      } else {
        alert('Test failed. Please check your configuration.');
      }
    } catch (error) {
      console.error('Error testing integration:', error);
      alert('Test failed. Please try again.');
    } finally {
      setTesting(false);
    }
  };

  const handleDeleteIntegration = async () => {
    if (window.confirm('Are you sure you want to delete the Slack integration?')) {
      try {
        const response = await fetch('/api/v1/slack/', {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          setIntegration(null);
          setActiveStep(0);
        }
      } catch (error) {
        console.error('Error deleting integration:', error);
      }
    }
  };

  const steps = [
    'Create Slack App',
    'Configure Integration',
    'Connect to Slack',
    'Test & Verify'
  ];

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Slack Integration
        </Typography>
        {integration ? (
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => setIsConfigDialogOpen(true)}
            >
              Settings
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={handleDeleteIntegration}
            >
              Remove Integration
            </Button>
          </Stack>
        ) : (
          <Button
            variant="contained"
            startIcon={<IntegrationInstructions />}
            onClick={() => setIsConfigDialogOpen(true)}
          >
            Setup Integration
          </Button>
        )}
      </Box>

      {/* Integration Status */}
      {integration ? (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title="Integration Status"
                avatar={<IntegrationInstructions color="primary" />}
              />
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Status</Typography>
                    <Chip
                      label={integration.is_enabled ? 'Active' : 'Disabled'}
                      color={integration.is_enabled ? 'success' : 'default'}
                    />
                  </Box>
                  {integration.team_name && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Workspace</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {integration.team_name}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Default Channel</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {integration.default_channel || 'Not set'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Installed</Typography>
                    <Typography variant="body2">
                      {new Date(integration.installed_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<Launch />}
                  onClick={handleTestIntegration}
                  disabled={testing}
                >
                  {testing ? 'Testing...' : 'Test Integration'}
                </Button>
                <Button
                  size="small"
                  startIcon={<Refresh />}
                  onClick={fetchChannels}
                >
                  Refresh Channels
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title="Feature Status"
                avatar={<Settings color="primary" />}
              />
              <CardContent>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      {integration.enable_conversation_notifications ? <Check color="success" /> : <Warning color="warning" />}
                    </ListItemIcon>
                    <ListItemText primary="Conversation Notifications" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {integration.enable_mention_notifications ? <Check color="success" /> : <Warning color="warning" />}
                    </ListItemIcon>
                    <ListItemText primary="Mention Notifications" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {integration.enable_message_replies ? <Check color="success" /> : <Warning color="warning" />}
                    </ListItemIcon>
                    <ListItemText primary="Message Replies" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {integration.enable_status_updates ? <Check color="success" /> : <Warning color="warning" />}
                    </ListItemIcon>
                    <ListItemText primary="Status Updates" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        /* Setup Stepper */
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Slack Integration Setup" />
          <CardContent>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                  <StepContent>
                    {index === 0 && (
                      <Box>
                        <Typography variant="body2" paragraph>
                          First, create a Slack app in your workspace with the required permissions.
                        </Typography>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            Go to <Link href="https://api.slack.com/apps" target="_blank">api.slack.com/apps</Link> and create a new app.
                            Add the following OAuth scopes: <code>chat:write</code>, <code>channels:read</code>, <code>groups:read</code>, <code>users:read</code>, <code>commands</code>
                          </Typography>
                        </Alert>
                        <Button onClick={() => setIsConfigDialogOpen(true)} variant="contained">
                          Configure App Details
                        </Button>
                      </Box>
                    )}
                    {index === 1 && (
                      <Box>
                        <Typography variant="body2" paragraph>
                          Configure your Slack app credentials and settings.
                        </Typography>
                        <Button onClick={handleOAuthConnect} variant="contained">
                          Connect to Slack
                        </Button>
                      </Box>
                    )}
                    {index === 2 && (
                      <Box>
                        <Typography variant="body2" paragraph>
                          Complete the OAuth flow to connect your Slack workspace.
                        </Typography>
                        <Button onClick={handleTestIntegration} variant="contained">
                          Test Integration
                        </Button>
                      </Box>
                    )}
                    {index === 3 && (
                      <Box>
                        <Typography variant="body2" paragraph>
                          Your Slack integration is ready! You'll receive notifications in your configured channel.
                        </Typography>
                        <Alert severity="success">
                          Integration setup complete! 🎉
                        </Alert>
                      </Box>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>
      )}

      {/* Available Commands */}
      <Card>
        <CardHeader title="Available Slack Commands" />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Slash Commands
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="/converse help"
                    secondary="Show available commands"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="/converse status"
                    secondary="Show your conversation status"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="/converse stats"
                    secondary="Show quick statistics"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="/cw stats"
                    secondary="Quick stats command"
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Bot Mentions
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="@converse help"
                    secondary="Show help message"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="@converse status"
                    secondary="Your current status"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="@converse assign [conv_id] [agent]"
                    secondary="Assign conversation"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="@converse resolve [conv_id]"
                    secondary="Resolve conversation"
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Configuration Dialog */}
      <Dialog open={isConfigDialogOpen} onClose={() => setIsConfigDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {integration ? 'Update Slack Integration' : 'Configure Slack Integration'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              {!integration && (
                <>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      You'll need these values from your Slack app settings at <Link href="https://api.slack.com/apps" target="_blank">api.slack.com/apps</Link>
                    </Alert>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Client ID"
                      value={formData.client_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_id: e.target.value }))}
                      helperText="Found in Basic Information > App Credentials"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Client Secret"
                      type="password"
                      value={formData.client_secret}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_secret: e.target.value }))}
                      helperText="Found in Basic Information > App Credentials"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Signing Secret"
                      type="password"
                      value={formData.signing_secret}
                      onChange={(e) => setFormData(prev => ({ ...prev, signing_secret: e.target.value }))}
                      helperText="Found in Basic Information > App Credentials"
                    />
                  </Grid>
                </>
              )}
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Default Channel</InputLabel>
                  <Select
                    value={formData.default_channel}
                    onChange={(e) => setFormData(prev => ({ ...prev, default_channel: e.target.value }))}
                  >
                    <MenuItem value="">None</MenuItem>
                    {channels.map((channel) => (
                      <MenuItem key={channel.id} value={`#${channel.name}`}>
                        #{channel.name} {channel.is_private && '(Private)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Feature Settings
                </Typography>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enable_conversation_notifications}
                        onChange={(e) => setFormData(prev => ({ ...prev, enable_conversation_notifications: e.target.checked }))}
                      />
                    }
                    label="Conversation Notifications"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enable_mention_notifications}
                        onChange={(e) => setFormData(prev => ({ ...prev, enable_mention_notifications: e.target.checked }))}
                      />
                    }
                    label="Mention Notifications"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enable_message_replies}
                        onChange={(e) => setFormData(prev => ({ ...prev, enable_message_replies: e.target.checked }))}
                      />
                    }
                    label="Message Replies"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enable_status_updates}
                        onChange={(e) => setFormData(prev => ({ ...prev, enable_status_updates: e.target.checked }))}
                      />
                    }
                    label="Status Updates"
                  />
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Notification Types
                </Typography>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notification_settings.new_conversation}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          notification_settings: {
                            ...prev.notification_settings,
                            new_conversation: e.target.checked
                          }
                        }))}
                      />
                    }
                    label="New Conversations"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notification_settings.conversation_assignment}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          notification_settings: {
                            ...prev.notification_settings,
                            conversation_assignment: e.target.checked
                          }
                        }))}
                      />
                    }
                    label="Conversation Assignments"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notification_settings.mention_in_note}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          notification_settings: {
                            ...prev.notification_settings,
                            mention_in_note: e.target.checked
                          }
                        }))}
                      />
                    }
                    label="Mentions in Notes"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notification_settings.high_priority_conversation}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          notification_settings: {
                            ...prev.notification_settings,
                            high_priority_conversation: e.target.checked
                          }
                        }))}
                      />
                    }
                    label="High Priority Conversations"
                  />
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfigDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={integration ? handleUpdateSettings : handleCreateIntegration}
            variant="contained"
          >
            {integration ? 'Update Settings' : 'Create Integration'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SlackIntegrationPage;